/**
 * Full-viewport loading screen: Olloport logo + progress bar over a breathing
 * gradient. CSS-only visuals — no poster image, nothing to download, so the
 * screen is on and animating from the instant it exists.
 *
 * Standalone page: index.html ships this exact markup statically
 * (#ollo-boot-loading) so the screen paints before the viewer bundle arrives;
 * the constructor ADOPTS that node — same DOM element, so the CSS animations
 * never restart and the hand-off is invisible. Embed mounts (no boot node)
 * get a freshly built copy inside the ui root. Markup + styles are duplicated
 * in index.html: KEEP IN SYNC.
 *
 * Progress contract (see mount.ts): setProgress is fed real, monotonic
 * display targets derived from actual network bytes; the bar glides toward
 * them with a CSS transition. 100% is only ever set via complete(), which
 * mount calls once the splat has ACTUALLY painted (pixel probe) — by design
 * there is no state where the bar is full but the scene is not on screen.
 * complete() finishes the bar fast and fades the overlay out, under 0.6 s.
 *
 * Everything that moves here is a CSS transition on transform or opacity:
 * those run on the compositor, so the bar keeps gliding and the fade keeps
 * fading while the main thread is stalled by the engine's uploads. (The
 * first version eased the bar per requestAnimationFrame, which froze with
 * the main thread — the "jittery loading bar", 12/10/2026.)
 *
 * Stand demo (`expectedMs` known from src/demo/load-clock.ts): the scene is
 * served from the device's own cache, so bytes land in a burst and say
 * nothing about the decode and upload that take the time. The bar is then
 * driven by the clock instead — elapsed over the last measured load of
 * this scene on this device — which is the honest picture of how far along
 * it is; it parks at 96 % if the load runs long, and complete() finishes it.
 */

import { resolveLang } from './lang';

const OLLO_LOGO_SVG = `<svg class="ollo-loading-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 751.48 329.5" aria-hidden="true">
  <path data-letter="1" d="M360.29,45.62v244.69c0,1.38-.55,2.7-1.52,3.68l-39.2,33.1c-1.83,1.54-4.14,2.39-6.53,2.39l-50.44.03c-2.87,0-5.2-2.33-5.2-5.2V5.2C257.4,2.33,259.72,0,262.6,0h52.3C316.27,0,317.59.55,318.56,1.52l40.2,40.42c.98.98,1.53,2.3,1.53,3.68Z"/>
  <path data-letter="2" d="M391.18,45.62v244.69c0,1.38.55,2.7,1.52,3.68l39.2,33.1c1.83,1.54,4.14,2.39,6.53,2.39h50.41c2.87,0,5.2-2.33,5.2-5.2l.03-319.07C494.08,2.33,491.75,0,488.88,0h-52.3c-1.38,0-2.7.55-3.67,1.52l-40.2,40.42c-.98.98-1.53,2.3-1.53,3.68Z"/>
  <path data-letter="0" d="M0,5.2v319.07c0,2.87,2.33,5.2,5.2,5.2h216.11c2.87,0,5.2-2.33,5.2-5.2V5.21c0-2.87-2.33-5.2-5.2-5.2L5.2,0C2.33,0,0,2.33,0,5.2ZM72.07,252.19V77.27c0-2.87,2.33-5.2,5.2-5.2h61.67c2.87,0,5.2,2.33,5.2,5.2v174.93c0,2.87-2.33,5.2-5.2,5.2h-61.67c-2.87,0-5.2-2.33-5.2-5.2Z"/>
  <path data-letter="3" d="M746.28,0l-216.11.02c-2.87,0-5.2,2.33-5.2,5.2v319.05c0,2.87,2.33,5.2,5.2,5.2h216.11c2.87,0,5.2-2.33,5.2-5.2V5.2c0-2.87-2.33-5.2-5.2-5.2ZM674.21,257.39h-61.67c-2.87,0-5.2-2.33-5.2-5.2V77.27c0-2.87,2.33-5.2,5.2-5.2h61.67c2.87,0,5.2,2.33,5.2,5.2v174.93c0,2.87-2.33,5.2-5.2,5.2Z"/>
</svg>`;

/** Length of one cycle of /logo-loop.mp4 (src/demo/logo-capture.ts): the
 *  loader's minimum life plus the fade, so one pass of gather, pack and
 *  dissolve fits a quick load, played at 1× — any other rate on a 60 Hz
 *  screen duplicates and drops frames unevenly, which judders by itself. */
const LOOP_S = 4;

/** Stand demo (Lenny, 13/10/2026, final word): no bar. The animation runs
 *  ONCE — a random point cloud gathers into OLLO, turns, evaporates — then
 *  black, and the scene comes in. The video is LOOP_S long and ends on
 *  black; black holds until the scene is ready (settled, full rendering
 *  back on), at least this long, then a short fade. */
const BLACK_HOLD_MS = 500;

export interface LoaderMetrics {
    /** Video frames the browser dropped while the loader was up, of how
     *  many it should have shown — the proof of a smooth (or not) loader. */
    droppedFrames: number;
    totalFrames: number;
}

const LOADER_MARKUP = `<div class="ollo-loading-logo-wrap">${OLLO_LOGO_SVG}</div>` +
    '<div class="ollo-loading-bar-track"><div class="ollo-loading-bar-fill"></div></div>';

/** Shown only when the download is genuinely slow. The bar easing to ~98% and
 *  waiting is honest — it IS still loading — but silence there reads as a hang,
 *  which is what the grey screen used to resolve into. */
const SLOW_NOTE = {
    en: 'Slow connection — still loading',
    es: 'Conexión lenta — seguimos cargando',
    ca: 'Connexió lenta — encara carregant'
} as const;

/** Shown when the load has genuinely died (no bytes, no frame change, no
 *  structure ever seen). Revealing the scene here painted the grey screen;
 *  an honest "reload" note on the loader is strictly better. */
const STALLED_NOTE = {
    en: 'Connection lost — reload to try again',
    es: 'Conexión perdida — recarga para reintentar',
    ca: 'Connexió perduda — recarrega per tornar-ho a provar'
} as const;

export class LoadingScreen {

    private root: HTMLDivElement;

    private barFill: HTMLDivElement;

    /** Last target fed to the bar (0..1), monotonic. */
    private target = 0;

    private completed = false;

    private note: HTMLDivElement | null = null;

    private hidden = false;

    private disposeTimer = 0;

    private clockDriven = false;

    private disposed = false;

    /** Demo: the animation, as a video (see the header). */
    private video: HTMLVideoElement | null = null;

    private readonly bornAt = performance.now();

    /**
     * @param opts.block3d  Stand demo: the flat logo becomes the OLLO block
     *   animation — /logo-loop.mp4, rendered once from ui/logo-block.ts.
     *   A video because on the devices it is decoded in hardware and
     *   composited by the system, untouched by the engine's main-thread
     *   stalls; a canvas animation froze with them, even from a worker.
     *   The loop plays at the rate that lands its dissolve on the expected
     *   load time. Falls back to the flat SVG if the video cannot play.
     * @param opts.expectedMs  How long this load is expected to take (last
     *   measured on this device), so the block's cycle can end with it.
     */
    private readonly once: boolean;

    private videoDone = false;

    private completedAt = 0;

    private readonly onMetrics: ((m: LoaderMetrics) => void) | null;

    private readonly onHide: (() => void) | null;

    private hideTimer = 0;

    /**
     * @param opts.video  Demo: a `<video>` the page keeps loaded between
     *   opens, so the loop starts on its first frame instead of spinning a
     *   decoder up each time. Moved into the loader; handed back on dispose.
     * @param opts.onMetrics  Demo: dropped-frame count when the loader goes.
     * @param opts.onHide  Called the moment the overlay starts to fade (after
     *   any hold) — the demo shows the joystick, which paints above the
     *   overlay, only from then.
     */
    constructor(parent: HTMLElement, opts: {
        block3d?: boolean; expectedMs?: number | null; video?: HTMLVideoElement | null;
        onMetrics?: (m: LoaderMetrics) => void; onHide?: () => void;
    } = {}) {
        this.once = !!opts.block3d;
        this.onMetrics = opts.onMetrics ?? null;
        this.onHide = opts.onHide ?? null;
        const boot = document.getElementById('ollo-boot-loading');
        if (boot instanceof HTMLDivElement) {
            // Adopt the static boot loader in place (it lives at body level,
            // NOT under `parent` — moving it would restart its animations).
            // Drop the id so a second mount on the same page can't adopt it too.
            this.root = boot;
            boot.removeAttribute('id');
            boot.classList.add('ollo-boot-adopted');
        } else {
            this.root = document.createElement('div');
            this.root.className = 'ollo-loading';
            parent.appendChild(this.root);
        }

        let fill = this.root.querySelector<HTMLDivElement>('.ollo-loading-bar-fill');
        if (!fill) {
            this.root.innerHTML = LOADER_MARKUP;
            fill = this.root.querySelector<HTMLDivElement>('.ollo-loading-bar-fill')!;
        }
        this.barFill = fill;

        const expected = opts.expectedMs ?? null;
        const plan = this.once ? 0 : (expected ?? 0);
        if (opts.block3d) {
            const wrap = this.root.querySelector<HTMLElement>('.ollo-loading-logo-wrap');
            if (wrap) {
                const v = opts.video ?? document.createElement('video');
                v.className = 'ollo-loading-video';
                v.muted = true;
                v.loop = false;       // once (see BLACK_HOLD_MS)
                v.playsInline = true;
                v.preload = 'auto';
                v.setAttribute('muted', '');
                v.setAttribute('playsinline', '');
                if (!v.getAttribute('src')) v.src = '/logo-loop.mp4';
                v.playbackRate = 1;   // see LOOP_S
                const giveUp = () => {
                    v.remove(); wrap.classList.remove('has-block'); this.root.classList.remove('has-video');
                    this.video = null; this.videoDone = true; this.maybeClose();
                };
                v.addEventListener('error', giveUp, { once: true });
                v.addEventListener('ended', () => { this.videoDone = true; this.maybeClose(); }, { once: true });
                try { v.currentTime = 0; } catch { /* not seekable yet */ }
                wrap.appendChild(v);
                wrap.classList.add('has-block');
                this.root.classList.add('has-video');
                void v.play().catch(giveUp);
                this.video = v;
            }
        }

        if (plan > 0) {
            // Demo: the clock drives the bar (see the header) — as ONE linear
            // transition to 96 % over the plan, started now. No timer, no
            // frame: a main-thread stall cannot pause it. (A 250 ms timer
            // loop was tried first and paused for a second at 54 % while
            // chunks applied.)
            this.clockDriven = true;
            requestAnimationFrame(() => {   // one frame after the initial transform has laid out
                if (this.disposed || this.completed) return;
                this.glide(0.96, plan);
            });
        }
    }

    /** Move the bar to `f` over `ms`, on the compositor. Monotonic. */
    private glide(f: number, ms: number): void {
        if (f <= this.target) return;
        this.target = f;
        this.barFill.style.transition = `transform ${ms}ms linear`;
        this.barFill.style.transform = `translateX(${((f - 1) * 100).toFixed(3)}%)`;
    }

    /** Feed a display target 0..1 (real bytes, mapped by mount). Monotonic:
     *  lower values are ignored, and nothing beats complete(). Ignored while
     *  the clock drives the bar (demo). */
    setProgress(fraction: number): void {
        if (this.completed || this.clockDriven) return;
        const f = Math.max(0, Math.min(1, fraction));
        // A bigger jump takes longer, so bursts of network updates read as
        // one glide rather than a stutter.
        this.glide(f, Math.max(250, Math.min(1200, (f - this.target) * 4000)));
    }

    /**
     * The splat has PAINTED (mount's pixel probe): run the bar to 100% in
     * 220 ms, then fade the whole overlay (the fade's CSS delay sequences
     * the two — nothing here waits on a frame or a timer that the engine's
     * first frames could starve). Splat fully visible ≤0.6 s from this call.
     */
    complete(): void {
        if (this.disposed || this.completed) return;
        this.completed = true;
        this.completedAt = performance.now();
        this.target = 1;
        if (this.once) { this.maybeClose(); return; }
        this.barFill.style.transition = 'transform 220ms ease-out';
        this.barFill.style.transform = 'translateX(0%)';
        // The fade waits for the bar: full bar first, then the scene.
        this.root.style.transitionDelay = '0.32s';
        this.root.classList.add('hidden');
        this.markHidden(320);
        // Removal only; the overlay is already transparent and never took
        // input, so a starved timer costs nothing visible.
        this.disposeTimer = window.setTimeout(() => this.dispose(), 1000);
    }

    /** Demo: the scene is ready AND the animation has run to black → hold
     *  the black (the engine's first full frames land behind it), then a
     *  short fade and the scene is there. */
    private maybeClose(): void {
        if (this.disposed || this.hidden || !this.completed || !this.videoDone) return;
        const hold = Math.max(250, BLACK_HOLD_MS - (performance.now() - this.completedAt));
        this.root.style.transitionDelay = `${(hold / 1000).toFixed(3)}s`;
        this.root.classList.add('hidden');
        this.markHidden(hold);
        this.disposeTimer = window.setTimeout(() => this.dispose(), hold + 700);
    }

    /** The overlay is on its way out; onHide once the fade actually starts. */
    private markHidden(delayMs: number): void {
        this.hidden = true;
        if (!this.onHide) return;
        if (delayMs <= 0) { this.onHide(); return; }
        this.hideTimer = window.setTimeout(() => { if (!this.disposed) this.onHide?.(); }, delayMs);
    }

    /**
     * The load is taking unusually long. Adds one quiet line under the bar;
     * idempotent, so the caller can fire it without tracking state.
     */
    showSlowNote(): void {
        if (this.disposed || this.completed || this.note) return;
        const n = document.createElement('div');
        n.className = 'ollo-loading-note';
        n.textContent = SLOW_NOTE[resolveLang()];
        this.root.appendChild(n);
        this.note = n;
    }

    /**
     * The load is dead (stagnation with a structureless frame, or the
     * absolute cap). Replaces the slow note if one is up; the overlay stays —
     * complete() is never called on this path unless the load recovers.
     */
    showStalledNote(): void {
        if (this.disposed || this.completed) return;
        if (this.note) { this.note.remove(); this.note = null; }
        const n = document.createElement('div');
        n.className = 'ollo-loading-note';
        n.textContent = STALLED_NOTE[resolveLang()];
        this.root.appendChild(n);
        this.note = n;
    }

    /** Abort path (scene failed): fade out without claiming completion. */
    finish(): void {
        if (this.disposed || this.hidden) return;
        this.markHidden(0);
        this.root.classList.add('hidden');
        this.disposeTimer = window.setTimeout(() => this.dispose(), 600);
    }

    dispose(): void {
        if (this.hideTimer) { window.clearTimeout(this.hideTimer); this.hideTimer = 0; if (!this.disposed) this.onHide?.(); }
        if (this.disposed) return;
        this.disposed = true;
        window.clearTimeout(this.disposeTimer);
        if (this.video) {
            const v = this.video;
            const q = (v as unknown as { getVideoPlaybackQuality?: () => { droppedVideoFrames: number; totalVideoFrames: number } }).getVideoPlaybackQuality?.();
            if (q && this.onMetrics) this.onMetrics({ droppedFrames: q.droppedVideoFrames, totalFrames: q.totalVideoFrames });
            v.pause();
            v.remove();   // the page keeps it loaded for the next open
        }
        this.root.remove();
    }
}
